/**
 * Every email Connect itself sends, as it is sent — for staff to read before a
 * client does.
 *
 * The API renders each one from its example with the real layout
 * (`GET /platform-mail/previews/{kind}`, `app.core.platform_mail`), so what is
 * shown here is the message, not a picture of it. The frame is sandboxed with
 * no scripts: the HTML is ours, but it is mail, and mail never runs anything.
 * Light and dark set the frame's colour scheme the way a reader's inbox would.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  LoadingState,
  Tabs,
  api,
} from '@connect/web-shared';

interface PreviewItem {
  kind: string;
  title: string;
  to: string;
  when: string;
  category: string;
  subject: string;
}

interface Preview {
  kind: string;
  subject: string;
  html: string;
  text: string;
}

type View = 'light' | 'dark' | 'text';

const VIEWS = [
  { id: 'light', label: 'Light inbox' },
  { id: 'dark', label: 'Dark inbox' },
  { id: 'text', label: 'Plain text' },
] as const;

function message(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

export function EmailPreviews() {
  const list = useQuery({
    queryKey: ['platform-mail', 'previews'],
    queryFn: () => api.get<PreviewItem[]>('/platform-mail/previews'),
  });
  const [picked, setPicked] = useState<string | null>(null);
  const [view, setView] = useState<View>('light');
  const [phone, setPhone] = useState(false);
  const kind = picked ?? list.data?.[0]?.kind ?? null;
  const item = list.data?.find((entry) => entry.kind === kind);

  const preview = useQuery({
    queryKey: ['platform-mail', 'preview', kind],
    queryFn: () => api.get<Preview>(`/platform-mail/previews/${kind}`),
    enabled: kind !== null,
  });

  if (list.isLoading) return <LoadingState label="Reading Connect's emails" />;
  if (list.isError) {
    return (
      <ErrorState
        message={message(list.error, 'The emails could not be read.')}
        onRetry={() => void list.refetch()}
      />
    );
  }

  return (
    <Card>
      <CardHeader
        title="Emails"
        description="Every email Connect itself sends to clients, partners and staff, with example details. Clients' own email to their customers is not here."
      />
      <CardBody>
        <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <ul className="space-y-1" aria-label="Emails">
            {(list.data ?? []).map((entry) => (
              <li key={entry.kind}>
                <button
                  type="button"
                  onClick={() => setPicked(entry.kind)}
                  aria-current={entry.kind === kind ? 'true' : undefined}
                  className={`w-full rounded-control px-3 py-2 text-left transition-colors ${
                    entry.kind === kind ? 'bg-accent-subtle' : 'hover:bg-surface-2'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-body">{entry.title}</span>
                    <Badge tone="neutral">{entry.category}</Badge>
                  </span>
                  <span className="mt-0.5 block text-2xs text-muted">{entry.to}</span>
                </button>
              </li>
            ))}
          </ul>

          <div className="min-w-0 space-y-3">
            {item ? (
              <div className="space-y-0.5 text-xs">
                <p className="font-medium text-body">{preview.data?.subject ?? item.subject}</p>
                <p className="text-muted">Sent when: {item.when}</p>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Tabs items={VIEWS} value={view} onChange={(id) => setView(id as View)} />
              {view === 'text' ? null : (
                <Button variant="ghost" size="sm" onClick={() => setPhone((on) => !on)}>
                  {phone ? 'Desktop width' : 'Phone width'}
                </Button>
              )}
            </div>
            {preview.isLoading ? (
              <LoadingState label="Rendering the email" />
            ) : preview.isError ? (
              <ErrorState
                message={message(preview.error, 'The email could not be rendered.')}
                onRetry={() => void preview.refetch()}
              />
            ) : preview.data ? (
              view === 'text' ? (
                <pre className="whitespace-pre-wrap rounded-control border border-line bg-surface-2 p-3 font-mono text-xs text-body">
                  {preview.data.text}
                </pre>
              ) : (
                <MailFrame html={preview.data.html} dark={view === 'dark'} phone={phone} />
              )
            ) : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/** The message in a frame as tall as the message, so the page scrolls, not it. */
function MailFrame({ html, dark, phone }: { html: string; dark: boolean; phone: boolean }) {
  const [height, setHeight] = useState(480);
  return (
    <div
      className="mx-auto overflow-hidden rounded-control border border-line"
      style={{ maxWidth: phone ? 375 : 640 }}
    >
      <iframe
        // A new frame per scheme: a frame's colour scheme is read when it loads.
        key={dark ? 'dark' : 'light'}
        title="Email preview"
        srcDoc={html}
        // Same origin only so the frame's height can be read; no scripts run.
        sandbox="allow-same-origin"
        onLoad={(event) => {
          const root = event.currentTarget.contentDocument?.documentElement;
          if (root) setHeight(root.scrollHeight);
        }}
        className="block w-full border-0"
        style={{ height, colorScheme: dark ? 'dark' : 'light', background: dark ? '#121212' : '#ffffff' }}
      />
    </div>
  );
}
